/**
 * For some reason, moment locale configuration does not work
 * when locales are imported from "moment/locale/*" with Vite.
 * @see https://github.com/moment/moment/issues/5926
 */
import 'moment/dist/locale/en-gb';
import 'moment/dist/locale/de';
import 'moment/dist/locale/de-at';
import 'moment/dist/locale/de-ch';
import 'moment/dist/locale/fr-ch';
import 'moment/dist/locale/fr';

import { GoogleOAuthProvider } from '@react-oauth/google';
import React, { useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Elements } from '@stripe/react-stripe-js';
import { createBrowserRouter, Route, RouterProvider, Routes } from 'react-router-dom';
import styled from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { Persistor } from 'redux-persist/es/types';
import { Store } from 'redux';

import { AuthenticationContextProvider } from '@front/auth/src/authentication-context';

import { ExperimentProvider } from '@front/common/src/providers/experiment-provider';
import { toRem } from '@front/common/src/utils/styles';
import colors from '@front/common/src/utils/colors';
import { getStripeLocale } from '@front/common/src/utils/stripe';
import { ConfigProvider } from '@front/common/src/providers/config-provider';
import { UniversesProvider } from '@front/common/src/providers/universes-provider';
import { getCurrentCountry } from '@front/common/src/utils/country';
import { initialVariants } from '@front/common/src/variants/initialVariants';
import { queryClient } from '@front/common/src/queries/query-client';

import {
  Experiment,
  getDeviceIdFromAmplitudeCookie,
  createExposureTracker,
} from '@front/web-ui/src/utils/amplitude-experiment';
import ScreenLoader from '@front/web-ui/src/components/screen-loader/screen-loader';
import { GlobalStyle } from '@front/web-ui/src/styles/global-style';
import { ModalProvider } from '@front/web-ui/src/components/modal/modal-container-with-backdrop';
import { minWidth } from '@front/web-ui/src/styles/mixins';
import ScrollToTop from '@front/web-ui/src/components/scroll-to-top/scroll-to-top';

import { connectUserToExternalSystems } from './utils/authentication';
import { AppContextProvider } from './contexts/app-context';
import { route } from './steps/steps';
import CleaningPromoModal from './components/promo-code/cleaning-promo-modal';
import MainContainer from './containers/main-container/main-container';
import Loader from './containers/loader/loader';
import config from './utils/config';

const headerHeight = toRem(56);

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white100};
  min-height: 100%;

  ${minWidth.md`
    min-height: calc(100% - ${headerHeight});
  `};
`;

if (import.meta.env.MODE === 'development' && !window.wecasa) {
  window.wecasa = {
    token: import.meta.env.VITE_REACT_APP_AUTH_TOKEN,
    mountpoint: route('root'),
    universe: import.meta.env.VITE_REACT_APP_UNIVERSE,
    siteUrl: import.meta.env.VITE_REACT_APP_SITE_URL,
    referer: import.meta.env.VITE_REACT_APP_REFERER,
    stripePublishableKey: import.meta.env.VITE_REACT_APP_STRIPE_PUBLISHABLE_KEY,
    admin_logged_in: import.meta.env.VITE_REACT_APP_IS_ADMIN_SIGN_IN,
    locale: import.meta.env.VITE_REACT_APP_LOCALE,
  };
}

queryClient.setDefaultOptions({
  queries: {
    ...queryClient.getDefaultOptions().queries,
    refetchOnWindowFocus: false,
  },
});

const stripePromise = window?.wecasa?.stripePublishableKey ? loadStripe(window.wecasa.stripePublishableKey) : null;
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const Root = () => (
  <SentryRoutes>
    <Route
      path='*'
      element={
        <>
          <ScrollToTop />
          <CleaningPromoModal />
          <MainContainer />
        </>
      }
    />
  </SentryRoutes>
);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([{ path: '*', Component: Root }], { basename: window.wecasa.mountpoint });

type AppProps = {
  persistor: Persistor;
  store: Store;
};

const App = ({ store, persistor }: AppProps) => {
  const [deviceId, setDeviceId] = useState<string>();

  useEffect(() => {
    setDeviceId(getDeviceIdFromAmplitudeCookie(config.AMPLITUDE_API_KEY));
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={config.GOOGLE_CLIENT_ID}>
        <ReactQueryDevtools initialIsOpen={false} />
        <Provider store={store}>
          <PersistGate loading={<ScreenLoader />} persistor={persistor}>
            {deviceId && (
              <ExperimentProvider
                experimentClient={Experiment.initialize(config.AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY, {
                  exposureTrackingProvider: createExposureTracker(),
                  fetchOnStart: false, // fetch will happen in loader, on user ready (signed in / signed out)
                  initialVariants: initialVariants[getCurrentCountry()],
                })}
                deviceId={deviceId}
              >
                <Elements stripe={stripePromise} options={{ locale: getStripeLocale() }}>
                  <GlobalStyle />
                  <ModalProvider>
                    <AuthenticationContextProvider onAuthenticated={connectUserToExternalSystems}>
                      <ConfigProvider>
                        <UniversesProvider>
                          <AppContextProvider>
                            <Loader>
                              <AppContainer>
                                <RouterProvider router={router} />
                              </AppContainer>
                            </Loader>
                          </AppContextProvider>
                        </UniversesProvider>
                      </ConfigProvider>
                    </AuthenticationContextProvider>
                  </ModalProvider>
                </Elements>
              </ExperimentProvider>
            )}
          </PersistGate>
        </Provider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
};

export default App;
